import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
    Box, Typography, Button, Card, CardContent, CardActions, Dialog, DialogTitle,
    DialogContent, DialogActions, CircularProgress, Alert, Grid, Divider,
    Autocomplete, TextField, Paper, IconButton, Select, MenuItem, FormControl, InputLabel,
    Accordion, AccordionSummary, AccordionDetails, Chip, Tooltip
} from '@mui/material';
import {
    Add as AddIcon, History as HistoryIcon, Description as DescriptionIcon,
    PostAdd as PostAddIcon, Edit as EditIcon, Delete as DeleteIcon,
    ArrowBack as ArrowBackIcon, Warning as WarningIcon, ExpandMore as ExpandMoreIcon,
    Visibility as ViewIcon, Category as CategoryIcon, Save as SaveIcon
} from '@mui/icons-material';
import { Editor } from '@tinymce/tinymce-react';

import { useAuth } from '../context/AuthContext';
import { getTemplates, getPatientById, deleteReport, deleteTemplate, updateTemplate } from '../services/reportService';

const API_URL = process.env.REACT_APP_API_URL || 'https://luiatro-health-cloud-production.up.railway.app';

const InformesPage = () => {
    const { patientId } = useParams();
    const navigate = useNavigate();
    const { user } = useAuth(); 
    const templateEditorRef = useRef(null);

    // --- VERIFICACIÓN DE ROL ROBUSTA ---
    // Detectamos si es admin sin importar mayúsculas o idioma
    const isAdmin = useMemo(() => {
        if (!user || !user.role) return false;
        const r = user.role.toLowerCase();
        return r === 'admin' || r === 'administrador';
    }, [user]);

    // Estados Datos
    const [allPatients, setAllPatients] = useState([]);
    const [isSelectionOpen, setIsSelectionOpen] = useState(false);
    const [patientData, setPatientData] = useState(null);
    const [templates, setTemplates] = useState([]);
    const [reports, setReports] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState(null);

    // Estados Modales Plantillas
    const [openTemplateModal, setOpenTemplateModal] = useState(false);
    const [isEditingTemplate, setIsEditingTemplate] = useState(false);
    const [currentTemplateId, setCurrentTemplateId] = useState(null);
    const [templateForm, setTemplateForm] = useState({ name: '', category: 'General', content: '' });
    const [isCreatingCategory, setIsCreatingCategory] = useState(false); 
    const [newCategoryName, setNewCategoryName] = useState('');

    // Estados Informes
    const [reportToReuse, setReportToReuse] = useState(null);
    const [isFetchingContent, setIsFetchingContent] = useState(false);
    const [luiatroDialog, setLuiatroDialog] = useState({ open: false, title: '', content: '', onConfirm: null });

    // CARGA DE DATOS
    const loadData = useCallback(async () => {
        if (!patientId) return;
        setIsLoading(true);
        try {
            const token = localStorage.getItem('token');
            const [pRes, tRes, rRes] = await Promise.all([
                getPatientById(patientId),
                getTemplates(),
                fetch(`${API_URL}/api/reports?patientId=${patientId}`, { headers: { 'Authorization': `Bearer ${token}` } })
            ]);

            setPatientData(pRes);
            setTemplates(Array.isArray(tRes) ? tRes : []);

            if (rRes.ok) {
                const reportsJson = await rRes.json();
                setReports(reportsJson);
            }
        } catch (err) {
            console.error(err);
            setError('Error al cargar datos.');
        } finally {
            setIsLoading(false);
        }
    }, [patientId]);

    useEffect(() => {
        if (!patientId) {
            setIsSelectionOpen(true);
            const token = localStorage.getItem('token');
            fetch(`${API_URL}/api/patients`, { headers: { 'Authorization': `Bearer ${token}` } })
                .then(res => res.ok ? res.json() : [])
                .then(data => setAllPatients(Array.isArray(data) ? data : []))
                .catch(() => setAllPatients([]));
        } else {
            setIsSelectionOpen(false);
            loadData();
        }
    }, [patientId, loadData]);

    // AGRUPACIÓN POR CATEGORÍA
    const groupedTemplates = useMemo(() => {
        return templates.reduce((acc, curr) => {
            const cat = curr.category || 'General';
            if (!acc[cat]) acc[cat] = [];
            acc[cat].push(curr);
            return acc;
        }, {});
    }, [templates]);

    const existingCategories = useMemo(() => {
        const cats = new Set(templates.map(t => t.category || 'General'));
        ['General', 'Urología', 'Ginecología', 'Ecografía', 'Cirugía'].forEach(c => cats.add(c));
        return Array.from(cats).sort();
    }, [templates]);

    // GESTIÓN DE PLANTILLAS
    const handleOpenCreateTemplate = () => {
        setTemplateForm({ name: '', category: 'General', content: '' });
        setIsEditingTemplate(false);
        setCurrentTemplateId(null);
        setIsCreatingCategory(false);
        setNewCategoryName('');
        setOpenTemplateModal(true);
    };

    const handleOpenEditTemplate = (tpl) => {
        setTemplateForm({ 
            name: tpl.name, 
            category: tpl.category, 
            content: tpl.bodyContent || tpl.content || '' 
        });
        setIsEditingTemplate(true);
        setCurrentTemplateId(tpl.id);
        setIsCreatingCategory(false);
        setOpenTemplateModal(true);
    };

    const handleSaveTemplate = async () => {
        if (!templateForm.name || !templateEditorRef.current) return;
        
        const content = templateEditorRef.current.getContent();
        const finalCategory = (isCreatingCategory && newCategoryName.trim()) 
            ? newCategoryName.trim() 
            : templateForm.category;

        const payload = {
            name: templateForm.name,
            category: finalCategory,
            content: content,
            createdBy: user.id
        };

        try {
            const token = localStorage.getItem('token');
            if (isEditingTemplate) {
                await updateTemplate(currentTemplateId, payload);
            } else {
                await fetch(`${API_URL}/api/templates`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
                    body: JSON.stringify(payload)
                });
            }
            setOpenTemplateModal(false);
            loadData();
        } catch (e) { 
            console.error(e);
            alert("Error al guardar la plantilla."); 
        }
    };

    const handleDeleteTemplate = (tpl) => {
        setLuiatroDialog({
            open: true,
            title: 'Eliminar Plantilla',
            content: `¿Eliminar plantilla "${tpl.name}"? Solo el administrador o el creador pueden hacer esto.`,
            onConfirm: async () => {
                try {
                    await deleteTemplate(tpl.id);
                    loadData();
                    setLuiatroDialog(prev => ({ ...prev, open: false }));
                } catch (e) {
                    alert("Error al eliminar la plantilla.");
                }
            }
        });
    };

    // ✅ FUNCIÓN DE PERMISOS CORREGIDA
    const canModify = (tpl) => {
        if (!user) return false;
        if (isAdmin) return true; // Usa la variable robusta calculada arriba
        return tpl.createdBy === user.id;
    };

    const handleUseTemplate = (tpl) => {
        navigate(`/editor-informe/${patientId}`, {
            state: { initialContent: tpl.bodyContent || tpl.content }
        });
    };

    // GESTIÓN DE INFORMES
    const confirmReuseReport = async () => {
        if (!reportToReuse) return;
        setIsFetchingContent(true);
        try {
            const token = localStorage.getItem('token');
            const res = await fetch(`${API_URL}/api/reports/${reportToReuse.id}`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            if (res.ok) {
                const fullReport = await res.json();
                const content = fullReport.content || fullReport.bodyContent || '';
                navigate(`/editor-informe/${patientId}`, {
                    state: { initialContent: content }
                });
            } else {
                setError("No se pudo recuperar el contenido.");
            }
        } catch (e) {
            setError("Error de conexión.");
        } finally {
            setIsFetchingContent(false);
            setReportToReuse(null);
        }
    };

    const handleDeleteReport = (report) => {
        setLuiatroDialog({
            open: true,
            title: 'Eliminar Informe',
            content: `¿Eliminar definitivamente el informe "${report.title}"?`,
            onConfirm: async () => {
                setIsLoading(true);
                await deleteReport(report.id);
                await loadData();
                setIsLoading(false);
                setLuiatroDialog(prev => ({ ...prev, open: false }));
            }
        });
    };

    const handlePatientSelect = (patient) => { 
        if (patient) { setIsSelectionOpen(false); navigate(`/informes/${patient.id}`); } 
    };

    if (isLoading) return <Box sx={{ p: 4, textAlign: 'center' }}><CircularProgress /></Box>;

    return (
        <Box sx={{ p: 3 }}>
            {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}

            {/* HEADER */}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3, p: 2, bgcolor: '#e3f2fd', borderRadius: 2 }}>
                <Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <Button startIcon={<ArrowBackIcon />} onClick={() => navigate('/dashboard')} variant="outlined" size="small">Inicio</Button>
                        <Typography variant="h5" color="primary" sx={{ fontWeight: 'bold' }}>
                            {patientData ? `${patientData.name} ${patientData.lastName}` : 'Paciente'}
                        </Typography>
                    </Box>
                    <Box sx={{ mt: 1, ml: 12 }}>
                        <Typography variant="body1"><strong>C.I.:</strong> {patientData?.cedula || patientData?.idNumber || '---'}</Typography>
                    </Box>
                </Box>
                <Box sx={{ display: 'flex', gap: 2 }}>
                    <Button variant="outlined" startIcon={<PostAddIcon />} onClick={handleOpenCreateTemplate}>Nueva Plantilla</Button>
                    <Button variant="contained" size="large" startIcon={<AddIcon />} onClick={() => navigate(`/editor-informe/${patientId}`)} sx={{ fontWeight: 'bold' }}>Nuevo Informe</Button>
                </Box>
            </Box>

            <Grid container spacing={3}>
                
                {/* --- SECCIÓN PLANTILLAS --- */}
                <Grid item xs={12} md={5}>
                    <Paper variant="outlined" sx={{ p: 2, bgcolor: '#f5f5f5', minHeight: '500px' }}>
                        <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <DescriptionIcon color="secondary" /> Plantillas Disponibles
                        </Typography>
                        <Divider sx={{ mb: 2 }} />

                        {templates.length === 0 ? (
                            <Typography variant="body2" color="textSecondary" align="center">No hay plantillas creadas.</Typography>
                        ) : (
                            Object.entries(groupedTemplates).map(([category, items]) => (
                                <Accordion key={category} disableGutters elevation={1} sx={{ mb: 1, '&:before': { display: 'none' } }}>
                                    <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ bgcolor: '#fff', borderBottom: '1px solid #eee' }}>
                                        <Box sx={{ display: 'flex', alignItems: 'center', width: '100%', justifyContent: 'space-between', pr: 2 }}>
                                            <Typography variant="subtitle2" fontWeight="bold" sx={{ textTransform: 'uppercase' }}>
                                                {category}
                                            </Typography>
                                            <Chip label={items.length} size="small" color="primary" variant="outlined" sx={{ height: 20, fontSize: '0.7rem' }} />
                                        </Box>
                                    </AccordionSummary>
                                    <AccordionDetails sx={{ p: 1, bgcolor: '#fafafa' }}>
                                        {items.map((tpl) => (
                                            <Card key={tpl.id} variant="outlined" sx={{ mb: 1, borderColor: '#ddd' }}>
                                                <CardContent sx={{ p: '8px 12px !important', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                                    <Typography variant="body2" sx={{ fontWeight: 500 }}>{tpl.name}</Typography>
                                                    <Box>
                                                        <Tooltip title="Usar esta plantilla">
                                                            <IconButton size="small" color="primary" onClick={() => handleUseTemplate(tpl)}>
                                                                <ViewIcon fontSize="small" />
                                                            </IconButton>
                                                        </Tooltip>

                                                        {/* BOTONES VISIBLES SI ES ADMIN O CREADOR */}
                                                        {canModify(tpl) && (
                                                            <>
                                                                <Tooltip title="Editar">
                                                                    <IconButton size="small" onClick={() => handleOpenEditTemplate(tpl)}>
                                                                        <EditIcon fontSize="small" />
                                                                    </IconButton>
                                                                </Tooltip>
                                                                <Tooltip title="Borrar">
                                                                    <IconButton size="small" color="error" onClick={() => handleDeleteTemplate(tpl)}>
                                                                        <DeleteIcon fontSize="small" />
                                                                    </IconButton>
                                                                </Tooltip>
                                                            </>
                                                        )}
                                                    </Box>
                                                </CardContent>
                                            </Card>
                                        ))}
                                    </AccordionDetails>
                                </Accordion>
                            ))
                        )}
                    </Paper>
                </Grid>

                {/* --- SECCIÓN HISTORIAL --- */}
                <Grid item xs={12} md={7}>
                    <Paper elevation={0} sx={{ p: 2 }}>
                        <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <HistoryIcon color="action" /> Historial de Informes
                        </Typography>
                        <Divider sx={{ mb: 2 }} />
                        
                        {reports.length === 0 && <Typography variant="body2" color="textSecondary" align="center">Este paciente no tiene informes guardados.</Typography>}
                        
                        {reports.map(r => (
                            <Card key={r.id} variant="outlined" sx={{ mb: 2, borderLeft: '4px solid #1976d2' }}>
                                <CardContent sx={{ pb: 1 }}>
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                        <Typography variant="h6" color="primary" sx={{ fontSize: '1.1rem' }}>{r.title}</Typography>
                                        <Chip label={new Date(r.updatedAt).toLocaleDateString()} size="small" variant="outlined" />
                                    </Box>
                                    <Typography variant="body2" color="textSecondary" sx={{ mt: 1, fontStyle: 'italic' }}>
                                        {r.reason || "Sin observación"}
                                    </Typography>
                                </CardContent>
                                <CardActions sx={{ bgcolor: '#f9f9f9', justifyContent: 'flex-end', px: 2 }}>
                                    <Button size="small" startIcon={<EditIcon />} onClick={() => setReportToReuse(r)}>Abrir / Editar</Button>
                                    <IconButton size="small" color="error" onClick={() => handleDeleteReport(r)}><DeleteIcon /></IconButton>
                                </CardActions>
                            </Card>
                        ))}
                    </Paper>
                </Grid>
            </Grid>

            {/* MODAL: CREAR / EDITAR PLANTILLA */}
            <Dialog open={openTemplateModal} onClose={() => setOpenTemplateModal(false)} fullWidth maxWidth="md">
                <DialogTitle>{isEditingTemplate ? 'Editar Plantilla' : 'Crear Nueva Plantilla'}</DialogTitle>
                <DialogContent dividers>
                    <Grid container spacing={2} sx={{ mb: 2 }}>
                        <Grid item xs={12} md={8}>
                            <TextField 
                                label="Título de la Plantilla" 
                                fullWidth 
                                value={templateForm.name} 
                                onChange={(e) => setTemplateForm({ ...templateForm, name: e.target.value })} 
                            />
                        </Grid>
                        <Grid item xs={12} md={4}>
                            <FormControl fullWidth>
                                <InputLabel>Categoría</InputLabel>
                                <Select 
                                    value={isCreatingCategory ? 'NEW_CAT' : templateForm.category} 
                                    label="Categoría" 
                                    onChange={(e) => {
                                        if (e.target.value === 'NEW_CAT') {
                                            setIsCreatingCategory(true);
                                        } else {
                                            setIsCreatingCategory(false);
                                            setTemplateForm({ ...templateForm, category: e.target.value });
                                        }
                                    }}
                                >
                                    {existingCategories.map(cat => (
                                        <MenuItem key={cat} value={cat}>{cat}</MenuItem>
                                    ))}
                                    
                                    {/* OPCIÓN ESPECIAL PARA ADMIN */}
                                    {isAdmin && [
                                        <Divider key="div" />,
                                        <MenuItem key="new" value="NEW_CAT" sx={{ color: 'primary.main', fontWeight: 'bold' }}>
                                            <AddIcon fontSize="small" sx={{ mr: 1 }} /> + CREAR NUEVA CATEGORÍA
                                        </MenuItem>
                                    ]}
                                </Select>
                            </FormControl>
                        </Grid>

                        {isCreatingCategory && (
                            <Grid item xs={12}>
                                <TextField
                                    label="Nombre de la Nueva Categoría"
                                    fullWidth
                                    autoFocus
                                    color="secondary"
                                    value={newCategoryName}
                                    onChange={(e) => setNewCategoryName(e.target.value)}
                                    helperText="Esta categoría estará disponible para futuras plantillas."
                                    InputProps={{
                                        startAdornment: <CategoryIcon color="action" sx={{ mr: 1 }} />
                                    }}
                                />
                            </Grid>
                        )}
                    </Grid>

                    <Editor
                        apiKey="fjnrhmkoepnr0f2fhfyok2y8c6grqx5bmj64mga6erbzi93q"
                        onInit={(evt, editor) => templateEditorRef.current = editor}
                        initialValue={templateForm.content}
                        init={{
                            height: 400,
                            menubar: false,
                            toolbar: 'lineheight | undo redo | fontfamily fontsize | bold italic underline | alignleft aligncenter alignright | bullist numlist',
                            plugins: ['advlist', 'autolink', 'lists', 'wordcount'],
                            content_style: 'body { font-family: Arial; font-size: 11pt; padding: 10px; } p { margin: 0 0 10px 0; }'
                        }}
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setOpenTemplateModal(false)}>Cancelar</Button>
                    <Button onClick={handleSaveTemplate} variant="contained" startIcon={<SaveIcon />}>
                        Guardar
                    </Button>
                </DialogActions>
            </Dialog>

            {/* OTROS MODALES (SELECCIÓN PACIENTE, REUTILIZAR, CONFIRMACIÓN) */}
            <Dialog open={isSelectionOpen} onClose={() => navigate('/dashboard')} fullWidth maxWidth="sm">
                <DialogTitle>Seleccionar Paciente</DialogTitle>
                <DialogContent dividers>
                    <Autocomplete 
                        options={allPatients} 
                        getOptionLabel={(p) => `${p.name} ${p.lastName}`} 
                        onChange={(e, v) => handlePatientSelect(v)} 
                        renderInput={(params) => <TextField {...params} label="Buscar..." autoFocus />} 
                    />
                </DialogContent>
                <DialogActions><Button onClick={() => navigate('/dashboard')}>Cancelar</Button></DialogActions>
            </Dialog>

            <Dialog open={!!reportToReuse} onClose={() => setReportToReuse(null)}>
                <DialogTitle>Reutilizar Informe</DialogTitle>
                <DialogContent>
                    <Typography gutterBottom>¿Desea crear un nuevo informe basado en "{reportToReuse?.title}"?</Typography>
                    {isFetchingContent && (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mt: 2, color: '#1976d2' }}>
                            <CircularProgress size={20} />
                            <Typography variant="body2">Recuperando contenido...</Typography>
                        </Box>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setReportToReuse(null)} disabled={isFetchingContent}>Cancelar</Button>
                    <Button variant="contained" onClick={confirmReuseReport} disabled={isFetchingContent}>
                        {isFetchingContent ? 'Cargando...' : 'Sí, Reutilizar'}
                    </Button>
                </DialogActions>
            </Dialog>

            <Dialog open={luiatroDialog.open} onClose={() => setLuiatroDialog(prev => ({ ...prev, open: false }))}>
                <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1, color: '#d32f2f', fontWeight: 'bold' }}>
                    <WarningIcon /> Luiatro Health dice:
                </DialogTitle>
                <DialogContent>
                    <Typography>{luiatroDialog.content}</Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setLuiatroDialog(prev => ({ ...prev, open: false }))}>Cancelar</Button>
                    <Button variant="contained" color="error" onClick={luiatroDialog.onConfirm}>Confirmar</Button>
                </DialogActions>
            </Dialog>

        </Box>
    );
};

export default InformesPage;